import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ENVIRONMENT } from '../../../../environment/environment';
import { ApiResponse, Pagination } from '../../models/api-response.model';
import {
    Faq,
    FaqModel,
    FaqFilterModel
} from '../../models/faq.model';

/**
 * FAQ Service
 * Following Single Responsibility Principle: This service is solely responsible for FAQ data operations
 * Following Dependency Inversion Principle: Depends on HttpClient abstraction, not concrete implementation
 *
 * Responsibility: Handle all FAQ-related API communications
 */
@Injectable({
    providedIn: 'root',
})
export class FaqService {
    private readonly apiRoute = `${ENVIRONMENT.apiDomain}/api/v1/faq`;

    constructor(private httpClient: HttpClient) {}

    /**
     * Fetch FAQ list with filtering and pagination
     * @param filterParams - Filter parameters for FAQ search
     * @returns Observable of paginated FAQ results
     */
    public getFaqList(body: FaqFilterModel): Observable<Pagination<FaqModel[]>> {
        return this.httpClient
            .post<ApiResponse<Pagination<Faq[]>>>(this.apiRoute, body)
            .pipe(
                map((response) => {
                    const resData = response.result.data.map((faq: Faq) => new FaqModel(faq));
                    return { ...response.result, data: resData };
                })
            );
    }

    /**
     * Get a single FAQ item by ID
     * @param id - FAQ item ID
     * @returns Observable of FAQ item
     */
    public getFaqById(id: string): Observable<FaqModel> {
        const apiPath = `${this.apiRoute}/${id}`;

        return this.httpClient
            .get<ApiResponse<Faq>>(apiPath)
            .pipe(map((response) => new FaqModel(response.result)));
    }

    /**
     * Get total count of published FAQs
     * @returns Observable of total count
     */
    public getFaqCount(): Observable<{ total: number }> {
        const apiPath = `${this.apiRoute}/published/total`;

        return this.httpClient
            .get<ApiResponse<{ total: number }>>(apiPath)
            .pipe(map((response) => response.result));
    }
}
